/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import gql from 'nanographql';
import Metrics from '@exc/metrics';
import {setShellInitDataForLogin, setShellInitDataForReload} from './UnifiedShellInit';

const gqlServiceEnvPoints = {
  dev: 'https://exc-unifiedcontent.experience-qa.adobe.com/api/graphql',
  prod: 'https://exc-unifiedcontent.experience.adobe.com/api/graphql',
  qa: 'https://exc-unifiedcontent.experience-qa.adobe.com/api/graphql',
  stage: 'https://exc-unifiedcontent.experience-stage.adobe.com/api/graphql',
  test: 'https://graphqlservice-dev-va7.dev.cloud.adobe.io/graphql'
};

const gqlServiceAdobeIoEnvPoints = {
  dev: 'https://graphqlservice-qa.adobe.io/graphql',
  prod: 'https://graphqlservice.adobe.io/graphql',
  qa: 'https://graphqlservice-qa.adobe.io/graphql',
  stage: 'https://graphqlservice-stage.adobe.io/graphql',
  test: 'https://graphqlservice-dev-va7.dev.cloud.adobe.io/graphql'
};

const GQL_FRAGMENTS = {
  imsSSOhandler: `
    fragment imsSSOhandlerFragment on ImsSSOhandlerResponse {
      redirectLocation
      message
      currentImsOrgId
    }`,
  userOrgs: `
    fragment userOrgsFragment on ImsOrganization {
      orgName
      orgRef {
        ident
        authSrc
      }
      countryCode
      orgType
    }`,
  userOrgSettings: `
    fragment userOrgSettingsFragment on CustomPreferencesResponse {
      level
      appId
      groupId
      userId
      orgId
      settings
    }`};

export const RELOAD_QUERY = `
  query shellInitDataQuery($locale: String, $userId: String!, $apiKey: String!, $userCurrentOrgId: String, $sessionUrl: String, $config_version: Int, $keys: [String]!) {
    shellInitData(sessionUrl: $sessionUrl) {
      userOrgs {
        ...userOrgsFragment
      }
      imsSessionData
      userBehance(userId: $userId, apiKey: $apiKey) {
        images
      }
      userOrgSettings(keys: $keys, userCurrentOrgId: $userCurrentOrgId, isLoggedIn: true ) {
        ...userOrgSettingsFragment
      }
      timeline(config_version: $config_version, userCurrentOrgId: $userCurrentOrgId, isLoggedIn: true, locale: $locale)
    }
  }
  ${GQL_FRAGMENTS.userOrgs}
  ${GQL_FRAGMENTS.userOrgSettings}
`;

export const LOGIN_QUERY = `
  query shellInitDataQuery($locale: String, $expiryTimeInterval: String, $redirectLocation: String, $userId: String!, $apiKey: String!, $config_version: Int, $keys: [String]!) {
    shellInitData(expiryTimeInterval: $expiryTimeInterval, redirectLocation: $redirectLocation) {
      userOrgs {
        ...userOrgsFragment
      }
      imsSSOhandler {
        ...imsSSOhandlerFragment
      }
      userBehance(userId: $userId, apiKey: $apiKey) {
        images
      }
      userOrgSettings(keys: $keys, isLoggedIn: false) {
        ...userOrgSettingsFragment
      }
      timeline(config_version: $config_version, isLoggedIn: false, locale: $locale)
    }
  }
  ${GQL_FRAGMENTS.userOrgs}
  ${GQL_FRAGMENTS.imsSSOhandler}
  ${GQL_FRAGMENTS.userOrgSettings}
`;

export class GraphQlService {
  constructor(env, clientId, accessToken) {
    this.url = `${gqlServiceEnvPoints[env]}?api_key=${clientId}`;
    this.adobeIoUrl = `${gqlServiceAdobeIoEnvPoints[env]}?api_key=${clientId}`;
    this.metrics = new Metrics('exc.core.GraphQlService');
    this.accessToken = accessToken;
  }

  initializeShellDataForLogin(variables) {
    return this.shellDataHandler(LOGIN_QUERY, {
      debugKey: 'User Login',
      store: setShellInitDataForLogin
    })(variables);
  }

  initializeShellDataForReload(variables) {
    return this.shellDataHandler(RELOAD_QUERY, {
      debugKey: 'App Re-load',
      store: setShellInitDataForReload
    })(variables);
  }

  async request(query, variables) {
    const msg = '[GraphQL Service Error in Shell Data Init]';
    try {
      let res;
      res = await fetch(this.url, {
        body: query(variables),
        'Content-Type': 'application/json',
        headers: {authorization: `Bearer ${this.accessToken}`},
        method: 'POST'
      });
      if (!res || !res.headers || (!res.headers.get('x-request-id') && !res.ok)) {
        this.metrics.error(`error in frontdoor call, hence making an adobeIO call`);
        res = await fetch(this.adobeIoUrl, {
          body: query(variables),
          'Content-Type': 'application/json',
          headers: {authorization: `Bearer ${this.accessToken}`},
          method: 'POST'
        });
      }
      const json = await res.json();
      ((json || {}).errors || []).map(err =>
        this.metrics.error(`${msg}: Message: ${err.message}`, err)
      );
      return json;
    } catch (networkError) {
      this.metrics.error(`${msg}: ${networkError}`);
    }
  }

  shellDataHandler(query, {debugKey, store}) {
    return async (variables) => {
      try {
        const response = await this.request(gql(query), variables);
        const data = ((response || {}).data || {}).shellInitData;
        if (data) {
          // set the response in session storage
          store(data, variables);
          this.metrics.info(`Initialized Shell Data required for ${debugKey} using GQL`);
        }
        return data;
      } catch (error) {
        this.metrics.error(`Failed to initialize Shell App Data for ${debugKey}`, error);
        return null;
      }
    };
  }
}
